import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import React, { Fragment } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import useIsTab from "../../hooks/useIsTab";
import useWindowSize from "../../hooks/useWindowSize";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ChatPanel } from "./ChatPanel";
import { ParticipantPanel } from "./ParticipantPanel";
import { WebinarParticipantPanel } from "./WebinarParticipantPanel";
import { PrompterPanel } from "./PrompterPanel";
import { Dialog, Transition } from "@headlessui/react";
import { useMediaQuery } from "react-responsive";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useSiteContext } from "../../context/SiteContext";

const SideBarTabView = ({
  height,
  sideBarContainerWidth,
  panelHeight,
  panelHeaderHeight,
  panelHeaderPadding,
  panelPadding,
  handleClose,
}) => {
  const { participants } = useMeeting();
  const { sideBarMode, participantApproveFunc, setParticipantApproveFunc } =
    useMeetingAppContext();
  const { meetingSettings, activeElement } = useSiteContext();
  const { publish: publishShowElementSection } = usePubSub("SHOW_ELEMENT_SECTION");

  return (
    <div
      className={`bg-gray-800 ${
        participantApproveFunc && participantApproveFunc.length > 0
          ? "z-[999]"
          : ""
      }`}
      style={{
        height,
        width: sideBarContainerWidth,
        paddingTop: panelPadding,
        paddingLeft: panelPadding,
        paddingRight: panelPadding,
        paddingBottom: panelPadding,
      }}
    >
      <div>
        <div
          className="bg-gray-750"
          style={{
            height: height,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <>
            {sideBarMode && (
              <div
                className={`flex items-center justify-between`}
                style={{
                  padding: panelHeaderPadding,
                  height: panelHeaderHeight - 1,
                  borderBottom: "1px solid #70707033",
                }}
              >
                <p className="text-base text-white font-bold">
                  {sideBarMode === "PARTICIPANTS"
                    ? `${
                        sideBarMode.charAt(0).toUpperCase() +
                          sideBarMode.slice(1).toLowerCase() || ""
                      } (${new Map(participants)?.size})`
                    : sideBarMode.charAt(0).toUpperCase() +
                        sideBarMode.slice(1).toLowerCase() || ""}
                </p>
                <div className="flex items-center gap-2">
                  {sideBarMode === "CHAT" && (
                    <button
                      className="px-3 py-1 text-xs text-white rounded-full "
                      onClick={() => {
                        try {
                          if (
                            typeof window !== "undefined" &&
                            typeof window.exportChatHistory === "function"
                          ) {
                            window.exportChatHistory();
                          }
                        } catch (e) {
                          console.error("Error triggering chat export", e);
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                      >
                        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />

                        <path d="M12 8v6" />
                        <path d="M9 11l3 3 3-3" />
                      </svg>
                    </button>
                  )}
                  {participantApproveFunc &&
                    participantApproveFunc.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                          onClick={() => {
                            participantApproveFunc.forEach((item) => {
                              item.allow();
                              if (activeElement) {
                                publishShowElementSection(
                                  JSON.stringify(activeElement),
                                  {
                                    persist: true,
                                  },
                                  activeElement
                                );
                              }
                            });
                            setTimeout(() => {
                              setParticipantApproveFunc([]);
                            }, 500);
                          }}
                        >
                          Admit All
                        </button>
                        <button
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                          onClick={() => {
                            participantApproveFunc.forEach((item) =>
                              item.deny()
                            );
                            setTimeout(() => {
                              setParticipantApproveFunc([]);
                            }, 500);
                          }}
                        >
                          Deny All
                        </button>
                      </div>
                    )}
                  <button
                    className="text-white"
                    onClick={handleClose}
                    style={{ margin: 0, padding: 0 }}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            {sideBarMode === "PARTICIPANTS" ? (
              meetingSettings?.roomType === "Webinar" ? (
                <WebinarParticipantPanel panelHeight={panelHeight} />
              ) : (
                <ParticipantPanel panelHeight={panelHeight} />
              )
            ) : sideBarMode === "CHAT" ? (
              <ChatPanel panelHeight={panelHeight} />
            ) : sideBarMode === "PROMPTER" ? (
              <PrompterPanel panelHeight={panelHeight} />
            ) : null}
          </>
        </div>
      </div>
    </div>
  );
};

export function SidebarConatiner({ height, sideBarContainerWidth }) {
  const { raisedHandsParticipants, sideBarMode, setSideBarMode } =
    useMeetingAppContext();
  const { prompterElement, activeElement } = useSiteContext();
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const windowSize = useWindowSize();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobile && isLandscapeOrientation;

  const panelPadding = 8;
  const topBarHeight = 64;
  const bottomBarHeight = 60;

  const paddedHeight = height - panelPadding * 3.5;

  // Calculate mobile panel height:
  // Parent passes height as (viewportHeight - bottomBarHeight),
  // so on mobile we should only subtract the top bar here.
  const mobilePanelHeight = isMobile || isTab ? height - topBarHeight : height;

  const actualMobileHeight =
    isMobile || isTab
      ? (windowSize?.height || window.innerHeight) -
      topBarHeight -
      bottomBarHeight
      : height;

  const panelHeaderHeight = isMobile
    ? 40
    : isTab
      ? 44
      : isLGDesktop
        ? 48
        : isXLDesktop
          ? 52
          : 0;

  const panelHeaderPadding = isMobile
    ? 6
    : isTab
      ? 8
      : isLGDesktop
        ? 10
        : isXLDesktop
          ? 12
          : 0;

  const handleClose = () => {
    console.log("HANDLE CLOSE", sideBarMode);
    setSideBarMode(null);
  };

  // Check if right CTA is active and should replace sidebar
  let isRightCtaActive = false;
  try {
    isRightCtaActive = activeElement?.type === "cta" &&
      activeElement?.settings &&
      JSON.parse(activeElement.settings)?.placement === "right" &&
      !isMobile && !isTab;
  } catch (e) {
    console.error("Error parsing CTA settings:", e);
  }

  // Sidebar should always show when sideBarMode is explicitly set (e.g., "CHAT", "PARTICIPANTS")
  // This allows chat/participants to toggle even when right CTA is active
  // The CTA will hide automatically when sidebar is open (handled in CtaSection)
  const shouldShowSidebar = !!sideBarMode; // Explicitly check if sideBarMode is truthy

  return (
    <>
      {sideBarMode && (isTab || isMobile) && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{
            opacity: sideBarMode ? 1 : 0,
            pointerEvents: sideBarMode ? "auto" : "none",
            transition: "opacity 0.3s ease-out",
          }}
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={handleClose}
          />
          {/* Panel */}
          <div
            className="fixed left-0 right-0 bg-gray-800 shadow-xl transform"
            style={{
              top: `${topBarHeight}px`,
              bottom: `${bottomBarHeight}px`,
              transform: sideBarMode ? "translateY(0)" : "translateY(100%)",
              transition: "transform 0.3s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SideBarTabView
              height={"100%"}
              sideBarContainerWidth={"100%"}
              panelHeight={Math.max(
                0,
                actualMobileHeight -
                panelHeaderHeight -
                panelHeaderPadding -
                panelPadding * 2
              )}
              raisedHandsParticipants={raisedHandsParticipants}
              panelHeaderHeight={panelHeaderHeight}
              panelHeaderPadding={panelHeaderPadding}
              panelPadding={panelPadding}
              handleClose={handleClose}
            />
          </div>
        </div>
      )}
      {/* Always show sidebar when sideBarMode is set, even if right CTA is active */}
      {/* Sidebar should appear above CTA, so use higher z-index */}
      {shouldShowSidebar && !isMobile && !isTab ? (
        <div
          className="fixed right-0 z-[60] pointer-events-auto"
          style={{
            top: `${topBarHeight}px`,
            bottom: `${bottomBarHeight}px`,
            width: `${sideBarContainerWidth}px`,
          }}
        >
          <SideBarTabView
            height={paddedHeight}
            sideBarContainerWidth={sideBarContainerWidth}
            panelHeight={paddedHeight - panelHeaderHeight - panelHeaderPadding}
            raisedHandsParticipants={raisedHandsParticipants}
            panelHeaderHeight={panelHeaderHeight}
            panelHeaderPadding={panelHeaderPadding}
            panelPadding={panelPadding}
            handleClose={handleClose}
          />
        </div>
      ) : null}
    </>
  );
}
